// 앱(Capacitor Android) 전용 화면 잠금. 로그인된 상태로 앱이 N분 이상 백그라운드에 있다가
// 다시 열리면(또는 콜드스타트) 전체화면으로 가리고 지문/얼굴(또는 기기 PIN/패턴)을 요구한다.
// 웹에서는 isNative()가 false라 아무 것도 렌더링하지 않는다 - NativePushBridge와 같은 자리에
// App.js 최상단에 한 번만 마운트한다.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Stack, Typography, Fade } from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { App } from '@capacitor/app';
import { logout } from '../../modules/auth';
import {
  isNative,
  isBiometricEnabled,
  authenticateBiometric,
  recordBackgroundedAt,
  clearBackgroundedAt,
  shouldLockNow,
  isAccessTokenExpired,
} from '../../lib/biometricLock';
import { requestRefresh } from '../../lib/api/client';

const BiometricLockScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(({ auth }) => auth.user);
  const [locked, setLocked] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);

  // 2026-10-16 에뮬레이터 실기기 테스트로 발견: BiometricAuth.authenticate()가 네이티브
  // AuthActivity를 따로 띄우는 바람에 우리 WebView의 MainActivity가 pause/resume을 한 번씩
  // 더 겪는다 — 그러면 아래 appStateChange 리스너가 "진짜 백그라운드"로 착각해서
  // recordBackgroundedAt()을 찍고, 인증 성공 직후 resume 이벤트가 다시 shouldLockNow()를
  // 걸어 막 해제한 잠금을 그 자리에서 다시 잠가버렸다(인증은 로그상 성공하는데 화면은 그대로
  // 잠겨 있는 것처럼 보임). 우리가 띄운 인증 흐름이 진행 중/직후인 동안은 appStateChange의
  // pause/resume을 전부 무시하도록 ref로 가드한다.
  const authFlowActiveRef = useRef(false);

  // 로그인 상태가 아니면(로그인 화면 자체) 잠금을 걸 이유가 없다.
  const activeGate = isNative() && !!user;

  useEffect(() => {
    if (!activeGate) {
      setLocked(false);
      return undefined;
    }

    // 콜드스타트/복귀 시점에 바로 한 번 판정 - 기록이 없으면 보수적으로 잠근다(biometricLock.js 참고).
    if (isBiometricEnabled() && shouldLockNow()) {
      setLocked(true);
    }

    const listenerPromise = App.addListener('appStateChange', ({ isActive }) => {
      if (authFlowActiveRef.current) return; // 우리가 띄운 인증 화면 때문에 생긴 전환 - 무시
      if (!isBiometricEnabled()) return;
      if (!isActive) {
        recordBackgroundedAt();
        return;
      }
      if (shouldLockNow()) {
        setLocked(true);
      }
    });

    return () => {
      listenerPromise?.then?.((handle) => handle.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGate]);

  const handleUnlock = useCallback(async () => {
    setAuthenticating(true);
    authFlowActiveRef.current = true;
    try {
      const ok = await authenticateBiometric('KMES 잠금을 해제하려면 인증하세요');
      if (ok) {
        clearBackgroundedAt();
        setLocked(false);

        // 액세스 토큰이 이미 만료돼 있으면 화면이 뜨자마자 401을 겪지 않도록 미리 한 번 갱신.
        try {
          const stored = localStorage.getItem('access_token');
          const token = stored ? JSON.parse(stored) : null;
          if (token && isAccessTokenExpired(token)) {
            await requestRefresh();
          }
        } catch (e) {
          // 리프레시 실패는 client.js의 401 인터셉터가 다음 API 호출에서 다시 처리한다.
        }
      }
      // 실패/취소 - locked 유지(아무것도 안 함)
    } finally {
      setAuthenticating(false);
      // AuthActivity가 닫히며 뒤따라오는 appStateChange(resume)가 살짝 늦게(비동기) 들어올 수
      // 있어 한 틱 더 가드를 유지한다 - 그 늦은 resume이 방금 푼 잠금을 다시 걸지 않도록.
      setTimeout(() => {
        authFlowActiveRef.current = false;
      }, 500);
    }
  }, []);

  const handleUsePassword = useCallback(() => {
    dispatch(logout());
    setLocked(false);
    navigate('/login');
  }, [dispatch, navigate]);

  if (!locked) return null;

  return (
    <Fade in={locked}>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 2147483000,
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
        }}
      >
        <Stack spacing={3} alignItems="center" sx={{ maxWidth: 320, textAlign: 'center' }}>
          <FingerprintIcon sx={{ fontSize: 72, color: 'primary.main' }} />
          <Typography variant="h6">KMES 잠금</Typography>
          <Typography variant="body2" color="text.secondary">
            계속하려면 지문/얼굴 인증(또는 기기 PIN/패턴)이 필요합니다.
          </Typography>
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={authenticating}
            onClick={handleUnlock}
            startIcon={<FingerprintIcon />}
          >
            {authenticating ? '인증 중...' : '지문으로 잠금 해제'}
          </Button>
          <Button variant="text" size="small" onClick={handleUsePassword}>
            비밀번호로 로그인
          </Button>
        </Stack>
      </Box>
    </Fade>
  );
};

export default BiometricLockScreen;
